import React, { useState } from "react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
	collection,
	addDoc,
	getDocs,
	deleteDoc,
	doc,
	updateDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import "./NewBlogPost.css";
import { useNavigate } from "react-router-dom";

function NewBlogPost() {
	const navigate = useNavigate();
	const [title, setTitle] = useState("");
	const [excerpt, setExcerpt] = useState("");
	const [content, setContent] = useState("");
	const [image, setImage] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [mode, setMode] = useState("create");
	const [blogPosts, setBlogPosts] = useState([]);
	const [selectedPost, setSelectedPost] = useState(null);

	const handleImageChange = (e) => {
		if (e.target.files[0]) {
			setImage(e.target.files[0]);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");

		try {
			let imageUrl = "/images/services-home-bg.jpeg";

			if (image) {
				const storage = getStorage();
				const storageRef = ref(
					storage,
					`blog-images/${Date.now()}-${image.name}`
				);

				const metadata = {
					contentType: image.type,
					customMetadata: {
						"Access-Control-Allow-Origin": "*",
					},
				};

				await uploadBytes(storageRef, image, metadata);
				imageUrl = await getDownloadURL(storageRef);
			}

			const blogPost = {
				title,
				excerpt,
				content,
				imageUrl,
				author: "Admin",
				date: new Date().toISOString(),
				readTime: `${Math.ceil(content.split(" ").length / 200)} min read`,
				createdAt: new Date(),
			};

			await addDoc(collection(db, "blogPosts"), blogPost);

			alert("Blog post created successfully!");
			navigate("/blog");
		} catch (error) {
			console.error("Error creating blog post:", error);
			setError("Error creating blog post. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const fetchBlogPosts = async () => {
		try {
			const querySnapshot = await getDocs(collection(db, "blogPosts"));
			const blogPostsList = querySnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setBlogPosts(blogPostsList);
		} catch (error) {
			console.error("Error fetching blog posts:", error);
			setError("Error fetching blog posts. Please try again.");
		}
	};

	const handleDelete = async (postId) => {
		if (window.confirm("Are you sure you want to delete this blog post?")) {
			try {
				await deleteDoc(doc(db, "blogPosts", postId));
				alert("Blog post deleted successfully!");
				fetchBlogPosts(); // Refresh the list
			} catch (error) {
				console.error("Error deleting blog post:", error);
				setError("Error deleting blog post. Please try again.");
			}
		}
	};

	const handleEditSelect = (post) => {
		setSelectedPost(post);
		setTitle(post.title);
		setExcerpt(post.excerpt);
		setContent(post.content);
		setMode("edit");
	};

	const handleUpdate = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");

		try {
			let imageUrl = selectedPost.imageUrl;

			if (image) {
				const storage = getStorage();
				const storageRef = ref(
					storage,
					`blog-images/${Date.now()}-${image.name}`
				);
				const metadata = {
					contentType: image.type,
					customMetadata: {
						"Access-Control-Allow-Origin": "*",
					},
				};

				await uploadBytes(storageRef, image, metadata);
				imageUrl = await getDownloadURL(storageRef);
			}

			const updatedPost = {
				title,
				excerpt,
				content,
				imageUrl,
				updatedAt: new Date(),
				readTime: `${Math.ceil(content.split(" ").length / 200)} min read`,
			};

			await updateDoc(doc(db, "blogPosts", selectedPost.id), updatedPost);
			alert("Blog post updated successfully!");
			setMode("delete");
			setSelectedPost(null);
			clearForm();
			fetchBlogPosts();
		} catch (error) {
			console.error("Error updating blog post:", error);
			setError("Error updating blog post. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const clearForm = () => {
		setTitle("");
		setExcerpt("");
		setContent("");
		setImage(null);
	};

	React.useEffect(() => {
		if (mode === "delete") {
			fetchBlogPosts();
		}
	}, [mode]);

	return (
		<div className="new-blog-post">
			<div className="mode-toggle">
				<button
					className={`toggle-btn ${mode === "create" ? "active" : ""}`}
					onClick={() => {
						setMode("create");
						clearForm();
						setSelectedPost(null);
					}}
				>
					Create Mode
				</button>
				<button
					className={`toggle-btn ${mode === "delete" ? "active" : ""}`}
					onClick={() => {
						setMode("delete");
						clearForm();
						setSelectedPost(null);
					}}
				>
					Edit Mode
				</button>
			</div>

			{mode === "create" ? (
				<>
					<h2>Create New Blog Post</h2>
					{error && <div className="error-message">{error}</div>}
					<form onSubmit={handleSubmit}>
						<div className="form-group">
							<label>Title</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								required
							/>
						</div>

						<div className="form-group">
							<label>Excerpt</label>
							<textarea
								value={excerpt}
								onChange={(e) => setExcerpt(e.target.value)}
								required
								rows="3"
								placeholder="Brief summary of your post..."
							/>
						</div>

						<div className="form-group">
							<label>Content</label>
							<textarea
								value={content}
								onChange={(e) => setContent(e.target.value)}
								required
								rows="10"
								placeholder="Write your blog post content here..."
							/>
						</div>

						<div className="form-group">
							<label>Featured Image</label>
							<input
								type="file"
								onChange={handleImageChange}
								accept="image/*"
								className="file-input"
							/>
						</div>

						<button type="submit" disabled={loading} className="submit-button">
							{loading ? "Creating..." : "Create Post"}
						</button>
					</form>
				</>
			) : mode === "edit" ? (
				<>
					<h2>Edit Blog Post</h2>
					{error && <div className="error-message">{error}</div>}
					<form onSubmit={handleUpdate}>
						<div className="form-group">
							<label>Title</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								required
							/>
						</div>

						<div className="form-group">
							<label>Excerpt</label>
							<textarea
								value={excerpt}
								onChange={(e) => setExcerpt(e.target.value)}
								required
								rows="3"
								placeholder="Brief summary of your post..."
							/>
						</div>

						<div className="form-group">
							<label>Content</label>
							<textarea
								value={content}
								onChange={(e) => setContent(e.target.value)}
								required
								rows="10"
								placeholder="Write your blog post content here..."
							/>
						</div>

						<div className="form-group">
							<label>Featured Image</label>
							<input
								type="file"
								onChange={handleImageChange}
								accept="image/*"
								className="file-input"
							/>
							{selectedPost.imageUrl && (
								<img
									src={selectedPost.imageUrl}
									alt={selectedPost.title}
									className="current-image-preview"
								/>
							)}
						</div>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<button
								type="submit"
								disabled={loading}
								className="submit-button"
							>
								{loading ? "Updating..." : "Update Post"}
							</button>
							<button
								type="button"
								onClick={() => {
									setMode("delete");
									clearForm();
									setSelectedPost(null);
								}}
								className="cancel-button"
							>
								Cancel
							</button>
						</div>
					</form>
				</>
			) : (
				<>
					<h2>Manage Blog Posts</h2>
					{error && <div className="error-message">{error}</div>}
					<div className="blog-posts-list">
						{blogPosts.map((post) => (
							<div key={post.id} className="blog-post-item">
								<h3>{post.title}</h3>
								<p>{post.excerpt}</p>
								<p className="post-meta">
									{new Date(post.date).toLocaleDateString()} • {post.readTime}
								</p>
								<div className="post-actions">
									<button
										onClick={() => handleEditSelect(post)}
										className="edit-button"
									>
										Edit
									</button>
									<button
										onClick={() => handleDelete(post.id)}
										className="delete-button"
									>
										Delete
									</button>
								</div>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
}

export default NewBlogPost;
